import { createClient } from '@supabase/supabase-js';

// Environment validation
const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Check your .env file.');
}

console.log('✅ Supabase configuration validated');

// Create Supabase client with optimized settings
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  },
  db: {
    schema: 'public'
  },
  global: {
    headers: { 'x-my-custom-header': 'college-management-system' },
  },
});

// Simplified connection test - single attempt only
export const testSupabaseConnection = async () => {
  try {
    const connectionRequest = supabase.from('user_profiles').select('id').limit(1);
    const timeout = new Promise((resolve) => {
      setTimeout(() => resolve({ error: { message: 'Connection timed out', code: 'TIMEOUT' } }), 10000);
    });
    const { error } = await Promise.race([connectionRequest, timeout]);
    
    if (error) {
      return {
        connected: false,
        error: `Database connection failed: ${error?.message}`,
        errorCode: error?.code || 'DATABASE_ERROR',
        suggestion: 'Check your Supabase project status and network connection.'
      };
    }
    
    return {
      connected: true,
      message: 'Database connection successful',
      errorCode: null
    };
  } catch (error) {
    return {
      connected: false,
      error: `Connection test failed: ${error?.message}`,
      errorCode: 'NETWORK_ERROR',
      suggestion: 'Verify your Supabase URL and API key in environment variables.'
    };
  }
};

// Simple network status check
export const detectNetworkStatus = async () => {
  try {
    if (!navigator.onLine) {
      return {
        online: false,
        error: 'No internet connection detected.',
        suggestion: 'Please check your network connection and try again.'
      };
    }
    
    // Quick network test
    const controller = new AbortController();
    let timeoutId = setTimeout(() => controller?.abort(), 5000);
    
    await fetch('https://httpbin.org/get', {
      method: 'GET',
      signal: controller?.signal
    });
    
    clearTimeout(timeoutId);
    
    return {
      online: true,
      message: 'Network connection active'
    };
  } catch (error) {
    return {
      online: false,
      error: 'Network connectivity issues detected.',
      suggestion: 'Check your internet connection or try again later.'
    };
  }
};

// Lightweight connection monitor - no aggressive intervals
export const createConnectionMonitor = (onStatusChange) => {
  let isMonitoring = false;
  let timeoutId = null;
  
  const checkConnection = async () => {
    if (!isMonitoring) return;
    
    try {
      const status = await testSupabaseConnection();
      onStatusChange(status);
      
      // Only schedule next check if there's an error
      if (!status?.connected && isMonitoring) {
        timeoutId = setTimeout(checkConnection, 30000); // 30 seconds
      }
    } catch (error) {
      console.warn('Connection monitor error:', error);
    }
  };
  
  return {
    start: () => {
      isMonitoring = true;
      checkConnection();
    },
    stop: () => {
      isMonitoring = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    },
    forceCheck: async () => {
      const status = await testSupabaseConnection();
      onStatusChange(status);
      return status;
    }
  };
};

export default supabase;
function resetConnectionCache(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: resetConnectionCache is not implemented yet.', args);
  return null;
}

export { resetConnectionCache };
